import * as THREE from 'three';

export const PULSE_SCENE_ID = 'pulse';

/** Declarative metadata for UI and preset defaults (standard export name for all scene modules). */
export const sceneMeta = {
  id: PULSE_SCENE_ID,
  label: 'Pulse',
  defaultBindings: {},
  paramSpecs: {
    zoom: { min: 1.5, max: 24, label: 'Zoom' },
  },
  defaultParams: {
    zoom: 5,
  },
  /** @type {{ key: string, label: string, min: number, max: number }[]} */
  bindingTargets: [
    { key: 'pulseDrive', label: 'BPM pulse drive', min: 0, max: 1 },
    { key: 'audioDrive', label: 'Audio reactivity', min: 0, max: 1 },
    { key: 'sphereBase', label: 'Sphere base size', min: 0, max: 1 },
    { key: 'ringExpand', label: 'Ring expansion', min: 0, max: 1 },
    { key: 'glow', label: 'Sphere brightness', min: 0, max: 1 },
  ],
};

/**
 * Sphere + wireframe ring driven by resolved preset bindings (0–1).
 */
export class PulseScene {
  constructor() {
    this.threeScene = new THREE.Scene();
    this.sphere = null;
    this.ring = null;
    this._camera = null;
    this._params = { ...sceneMeta.defaultParams };
    this._inited = false;
  }

  /** @deprecated use sceneMeta.bindingTargets */
  getMidiTargets() {
    return sceneMeta.bindingTargets;
  }

  getSceneLabel() {
    return sceneMeta.label;
  }

  _syncCameraZoom() {
    if (!this._camera) return;
    const z = Number(this._params.zoom);
    if (!Number.isFinite(z)) return;
    this._camera.position.z = z;
  }

  /**
   * @param {{ zoom?: number }} [params]
   */
  applyParams(params = {}) {
    this._params = { ...sceneMeta.defaultParams, ...params };
    this._syncCameraZoom();
  }

  /**
   * @param {{ getSize?: () => { w: number, h: number }, getCamera?: () => import('three').Camera }} [ctx]
   */
  init(ctx) {
    if (this._inited) return;
    this._camera = typeof ctx?.getCamera === 'function' ? ctx.getCamera() : null;
    this._syncCameraZoom();
    this._build();
    this._inited = true;
  }

  _build() {
    this.sphere = new THREE.Mesh(
      new THREE.SphereGeometry(1, 48, 48),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.3,
      })
    );
    this.threeScene.add(this.sphere);

    this.ring = new THREE.Mesh(
      new THREE.SphereGeometry(1, 24, 24),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        wireframe: true,
        transparent: true,
        opacity: 0.08,
      })
    );
    this.threeScene.add(this.ring);
  }

  /**
   * @param {number} _dt
   * @param {Record<string, number>} resolved preset-local signals (0–1); engine injects `_bpmPulse`, `_audioLevel`
   */
  update(_dt, resolved = {}) {
    const p = resolved;
    const bpmPulse = p._bpmPulse ?? 0;
    const audioLevel = p._audioLevel ?? 0;
    const pulseM = p.pulseDrive !== undefined ? p.pulseDrive : 1;
    const audioM = p.audioDrive !== undefined ? p.audioDrive : 1;
    const base =
      p.sphereBase !== undefined ? 0.15 + p.sphereBase * 0.45 : 0.35;

    const sphereScale =
      base + bpmPulse * 0.18 * pulseM + audioLevel * 0.5 * audioM;
    this.sphere.scale.setScalar(sphereScale);

    const glow = p.glow !== undefined ? p.glow : 1;
    const opCore = 0.3 + bpmPulse * 0.7 * pulseM;
    this.sphere.material.opacity = Math.min(1, opCore * (0.35 + glow * 0.65));

    const ringExtra = p.ringExpand !== undefined ? p.ringExpand * 0.6 : 0;
    const ringScale = base * 2.2 + audioLevel * 1.0 * audioM + ringExtra;
    this.ring.scale.setScalar(ringScale);
    this.ring.material.opacity = 0.05 + bpmPulse * 0.15 * pulseM;
  }

  resize(_w, _h) {}

  dispose() {
    if (this.sphere) {
      this.sphere.geometry.dispose();
      this.sphere.material.dispose();
    }
    if (this.ring) {
      this.ring.geometry.dispose();
      this.ring.material.dispose();
    }
    this.sphere = null;
    this.ring = null;
    this._camera = null;
    this._inited = false;
  }
}
